import { useCallback, useState } from "react";

import { useDispatch } from "react-redux";

import { useObjectMemo } from "../../../hooks";
import {
  GENERIC_API_ERROR,
  parseErrorResponse,
  useRequestWithFeedback,
  type OverrideRunProps,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { REQUEST_STATUS } from "../../../composites/request/request.types";

import { API } from "./api";
import { actions } from "./slice";
import {
  RecipientProfileForm,
  ACHDestinationForm,
  WireDestinationForm,
  transformWireFormFieldsToApiFields,
  transformACHFormFieldsToApiFields,
  transformSwiftFormFieldsToApiFields,
} from "./forms";
import { useSwiftDestinationContext } from "./forms/SwiftDestinationFormProvider";

import type { ApiError } from "../../../../types/models/v1";
import type { BootstrapFetchRequest } from "../../bootstrap";

export const useFetchRecipients = ({
  includeHidden = false,
}: { includeHidden?: boolean } = {}) => {
  const dispatch = useDispatch();

  const { send, status } =
    useRequestWithFeedback<API.FetchRecipients.Response>();

  const fetchRecipients = useCallback(
    (overrideRunProps: OverrideRunProps<API.FetchRecipients.Response> = {}) => {
      send({
        action: API.fetchRecipients({ includeHidden }),
        onData: ({ results: recipients }) => {
          dispatch(actions.setMany(recipients));
        },
        ...overrideRunProps,
      });
    },
    [dispatch, send, includeHidden],
  );

  return useObjectMemo({
    send: fetchRecipients,
    loading:
      status === REQUEST_STATUS.idle || status === REQUEST_STATUS.loading,
    error: status === REQUEST_STATUS.error,
    hasData: false,
  }) satisfies BootstrapFetchRequest;
};

export const useDeleteRecipient = () => {
  const dispatch = useDispatch();
  const { throwToast } = useLibrary("toasts");
  const { send, loading } = useRequestWithFeedback<
    API.FetchRecipients.Response,
    ApiError
  >();
  const t = useLibrary("translations");

  const deleteRecipient = useCallback(
    (
      recipientId: API.RecipientId,
      overrideRunProps: OverrideRunProps<API.FetchRecipients.Response> = {},
    ) => {
      send({
        action: API.deleteRecipient(recipientId),
        onData: () => {
          dispatch(actions.deleteOne(recipientId));
        },
        onError: async (err) => {
          let message = t.getString(
            "recipient-delete-error-banner",
            null,
            "Unable to delete recipient. Please try again.",
          );
          if (err.response?.status === 409) {
            message = t.getString(
              "recipient-delete-conflict-error-banner",
              null,
              "You cannot delete this recipient due to pending payment(s).",
            );
          }
          throwToast({
            kind: "error",
            message,
          });
        },
        messaging: {
          toast: {
            success: t.getString(
              "recipient-delete-success-banner",
              null,
              "Recipient deleted.",
            ),
          },
        },
        ...overrideRunProps,
      });
    },
    [dispatch, send, t, throwToast],
  );

  return useObjectMemo({
    deleteRecipient,
    loading,
  });
};

export const useCreateOrUpdateACHDestination = () => {
  const { send, loading, errorMessage } = useRequestWithFeedback<
    API.CreateUpdateACHDestination.Response,
    API.CreateUpdateACHDestination.Error
  >();
  const dispatch = useDispatch();
  const { values } = ACHDestinationForm.useForm();

  const createOrUpdateACHDestination = useCallback(
    (
      recipient: API.Recipient,
      overrideRunProps: OverrideRunProps<
        API.CreateUpdateACHDestination.Response,
        API.CreateUpdateACHDestination.Error
      > = {},
    ) => {
      const updatedValues = transformACHFormFieldsToApiFields({
        ...values,
        recipientId: recipient.id,
      });
      return send({
        action: API.createOrUpdateACHDestination(updatedValues),
        onData: (achDestination) => {
          // update the store by updating the recipient's ach destination details
          dispatch(
            actions.updateOne({
              ...recipient,
              ach_destination: achDestination,
            }),
          );
        },
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
        },
        ...overrideRunProps,
      });
    },
    [dispatch, send, values],
  );

  return useObjectMemo({
    errorMessage,
    createOrUpdateACHDestination,
    loading,
  });
};

export const useCreateOrUpdateWireDestination = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateUpdateWireDestination.Response,
    API.CreateUpdateWireDestination.Error
  >();
  const dispatch = useDispatch();
  const { values } = WireDestinationForm.useForm();

  const createWireDestination = useCallback(
    async (
      recipient: API.Recipient,
      overrideRunProps: OverrideRunProps<
        API.CreateUpdateWireDestination.Response,
        API.CreateUpdateWireDestination.Error
      > = {},
    ) => {
      const updatedValues = transformWireFormFieldsToApiFields({
        ...values,
        recipientId: recipient.id,
      });
      return send({
        action: API.createWireDestination(updatedValues),
        onData: (wireDestination) => {
          dispatch(
            actions.updateOne({
              ...recipient,
              fedwire_destination: wireDestination,
            }),
          );
        },
        ...overrideRunProps,
      });
    },
    [dispatch, send, values],
  );

  const updateWireDestination = useCallback(
    async (
      recipient: API.Recipient,
      overrideRunProps: OverrideRunProps<
        API.CreateUpdateWireDestination.Response,
        API.CreateUpdateWireDestination.Error
      > = {},
    ) => {
      const updatedValues = transformWireFormFieldsToApiFields({
        ...values,
        recipientId: recipient.id,
      });
      return send({
        action: API.updateWireDestination({
          ...updatedValues,
          fedwireId: recipient?.fedwire_destination
            ?.id as API.FedwireDestinationId,
        }),
        onData: (wireDestination) => {
          dispatch(
            actions.updateOne({
              ...recipient,
              fedwire_destination: wireDestination,
            }),
          );
        },
        ...overrideRunProps,
      });
    },
    [dispatch, send, values],
  );

  return useObjectMemo({
    createWireDestination,
    updateWireDestination,
    loading,
  });
};

export const useCreateOrUpdateRecipientProfile = () => {
  const { send, loading, status, errorMessage } = useRequestWithFeedback<
    API.CreateRecipientProfile.Response,
    API.CreateRecipientProfile.Error
  >();
  const { values } = RecipientProfileForm.useForm();
  const dispatch = useDispatch();

  const createOrUpdateRecipientProfile = useCallback(
    (
      recipientId?: API.RecipientId,
      overrideRunProps: OverrideRunProps<
        API.CreateRecipientProfile.Response,
        API.CreateRecipientProfile.Error
      > = {},
    ) => {
      return send({
        action: API.createOrUpdateRecipientProfile(values, recipientId),
        onData: (recipient) => {
          if (!recipientId) {
            dispatch(actions.setOne(recipient));
          } else {
            dispatch(actions.updateOne(recipient));
          }
        },
        ...overrideRunProps,
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
          if (overrideRunProps.onError) {
            overrideRunProps.onError(error, () => {});
          }
        },
      });
    },
    [dispatch, send, values],
  );

  return useObjectMemo({
    createOrUpdateRecipientProfile,
    loading,
    error: status === "error",
    errorMessage,
  });
};

export const useCreateOrUpdateSwiftDestination = () => {
  const [formErrors, setFormErrors] =
    useState<API.CreateUpdateSwiftDestination.Error | null>(null);
  const { send, loading } = useRequestWithFeedback<
    API.CreateUpdateSwiftDestination.Response,
    API.CreateUpdateSwiftDestination.Error
  >();
  const dispatch = useDispatch();
  const { getValues } = useSwiftDestinationContext();

  const createSwiftDestination = useCallback(
    (
      recipient: API.Recipient,
      overrideRunProps: OverrideRunProps<
        API.CreateUpdateSwiftDestination.Response,
        API.CreateUpdateSwiftDestination.Error
      > = {},
    ) => {
      const updatedValues = {
        ...transformSwiftFormFieldsToApiFields(getValues()),
        recipient: recipient?.id,
      };
      return send({
        action: API.createSwiftDestination(updatedValues),
        onData: (swiftDestination) => {
          dispatch(
            actions.updateOne({
              ...recipient,
              swift_destination: swiftDestination,
            }),
          );
          setFormErrors(null);
        },
        onError: async (error) => {
          try {
            const errorsMapped =
              (await error?.response?.json()) || GENERIC_API_ERROR;
            setFormErrors(errorsMapped);
          } catch (err) {
            setFormErrors(GENERIC_API_ERROR);
          }
          if (overrideRunProps.onError) {
            overrideRunProps.onError(error, () => {});
          }
        },
        ...overrideRunProps,
      });
    },
    [dispatch, send, getValues],
  );

  return useObjectMemo({
    createSwiftDestination,
    loading,
    formErrors,
    setFormErrors,
  });
};

export const useFetchSwiftAdditionalDetails = () => {
  const { send, loading, errorMessage } = useRequestWithFeedback<
    API.FetchSwiftAdditionalDetails.Response,
    API.FetchSwiftAdditionalDetails.Error
  >();

  const fetchSwiftAdditionalDetails = useCallback(
    (
      bic: string,
      overrideRunProps: OverrideRunProps<
        API.FetchSwiftAdditionalDetails.Response,
        API.FetchSwiftAdditionalDetails.Error
      > = {},
    ) => {
      return send({
        action: API.fetchSwiftAdditionalDetails(bic),
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
        },
        ...overrideRunProps,
      });
    },
    [send],
  );

  return useObjectMemo({
    fetchSwiftAdditionalDetails,
    loading,
    errorMessage,
  });
};

export const useFetchSwiftAdditionalFieldsByCountry = () => {
  const { send, loading, errorMessage } = useRequestWithFeedback<
    API.FetchSwiftAdditionalFieldsByCountry.Response,
    API.FetchSwiftAdditionalFieldsByCountry.Error
  >();

  const fetchSwiftAdditionalFieldsByCountry = useCallback(
    (
      countryCode: string,
      overrideRunProps: OverrideRunProps<
        API.FetchSwiftAdditionalFieldsByCountry.Response,
        API.FetchSwiftAdditionalFieldsByCountry.Error
      > = {},
    ) => {
      return send({
        action: API.fetchSwiftAdditionalFieldsByCountry(countryCode),
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
        },
        ...overrideRunProps,
      });
    },
    [send],
  );

  return useObjectMemo({
    fetchSwiftAdditionalFieldsByCountry,
    loading,
    errorMessage,
  });
};

export const useFetchRecipientAddressRenameFields = () => {
  const { send, loading, errorMessage } = useRequestWithFeedback<
    API.FetchRecipientAddressRenameFields.Response,
    API.FetchRecipientAddressRenameFields.Error
  >();

  const fetchRecipientAddressRenameFields = useCallback(
    (
      countryCode: string,
      overrideRunProps: OverrideRunProps<
        API.FetchRecipientAddressRenameFields.Response,
        API.FetchRecipientAddressRenameFields.Error
      > = {},
    ) => {
      return send({
        action: API.fetchRecipientAddressRenameFields(countryCode),
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
        },
        ...overrideRunProps,
      });
    },
    [send],
  );

  return useObjectMemo({
    fetchRecipientAddressRenameFields,
    loading,
    errorMessage,
  });
};
